import type { DownloadOutcome } from '@atlas/ui-domain'
import { triggerStaggeredDownloads } from '@/lib/browser-download'
import { fileService } from '@/services/file.service'

/** What the run needs per asset: the id to request a URL for, and the name to report a failure by. */
interface DownloadableAsset {
	id: string
	fileName: string
}

/**
 * Download several assets as several browser downloads: one fresh pre-signed URL per asset, all
 * signed in parallel, then handed to the browser in the order given.
 *
 * The download action a `@atlas/ui-domain` card asks its host for. Signing happens BEFORE any
 * hand-off because a round trip between two of them spends the transient user activation the
 * button press granted, after which the browser may suppress the rest without telling the page -
 * and the card would still report them as started. `triggerStaggeredDownloads` is the other half
 * of the same problem: hand-offs fired in one tick get coalesced or dropped.
 *
 * No URL is cached, stored or reused - the URL is itself the credential - and no asset's body is
 * ever fetched into the app, which would attach Atlas credentials to it. The server's
 * `Content-Disposition` stays the filename authority.
 *
 * A failed URL request costs its own asset and nothing else: the run continues and names what it
 * could not start.
 */
export const downloadAssets = async (assets: DownloadableAsset[]): Promise<DownloadOutcome> => {
	const signed = await Promise.all(
		assets.map((asset) =>
			fileService
				.getDownloadUrl(asset.id)
				.then((url) => ({ asset, url: url as string | null }))
				.catch(() => ({ asset, url: null })),
		),
	)

	return {
		// The same falsy test `triggerStaggeredDownloads` skips on, so an unusable URL is named
		// here rather than dropped from both counts: an empty string is no more downloadable
		// than a null, and reporting N started for N-1 files names nothing to retry.
		failedFileNames: signed.filter(({ url }) => !url).map(({ asset }) => asset.fileName),
		startedCount: await triggerStaggeredDownloads(signed.map(({ url }) => url)),
	}
}
